import CardHeader from "components/CardHeader";
import { Tenant } from "models/Tenant";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, CardBody, FormGroup, Input, Label, Spinner } from "reactstrap";
import { updateTenantInvoiceSettings } from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const defaultTemplate = "{year}-{month}";
const defaultInvoicePrefix = "INV";
const placeholders = ["{year}", "{month}", "{weeknr}", "{project.name}"];

const isoWeek = (date: Date) => {
    const value = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = value.getUTCDay() || 7;
    value.setUTCDate(value.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(value.getUTCFullYear(), 0, 1));
    return Math.ceil((((value.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

export const renderInvoiceNamePreview = (template: string, date: Date, projectName: string) =>
    (template.trim() || defaultTemplate)
        .replaceAll("{year}", String(date.getFullYear()))
        .replaceAll("{month}", String(date.getMonth() + 1).padStart(2, "0"))
        .replaceAll("{weeknr}", String(isoWeek(date)))
        .replaceAll("{project.name}", projectName);

const TenantInvoiceSettings: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
    const { t } = useOfficeTranslation();
    const [template, setTemplate] = useState(tenant.billing?.invoice_name_template || defaultTemplate);
    const [prefix, setPrefix] = useState(tenant.billing?.invoice_prefix || defaultInvoicePrefix);
    const [remindersEnabled, setRemindersEnabled] = useState(tenant.billing?.invoice_reminders_enabled !== false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);
    const preview = useMemo(
        () => renderInvoiceNamePreview(template, new Date(), "De Kring Roosendaal"),
        [template],
    );
    const invoiceNumberYear = new Date().getFullYear();

    useEffect(() => {
        setTemplate(tenant.billing?.invoice_name_template || defaultTemplate);
        setPrefix(tenant.billing?.invoice_prefix || defaultInvoicePrefix);
        setRemindersEnabled(tenant.billing?.invoice_reminders_enabled !== false);
    }, [tenant]);

    const save = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const result = await updateTenantInvoiceSettings(tenant.id, template, prefix, remindersEnabled);
            setTemplate(result.invoice_name_template);
            setPrefix(result.invoice_prefix);
            setRemindersEnabled(result.invoice_reminders_enabled);
            setMessage({ color: "success", text: t("tenants.tenantInvoiceSettings.settingsSaved") });
        } catch (reason: any) {
            setMessage({
                color: "danger",
                text: reason.response?.data?.error ?? t("tenants.tenantInvoiceSettings.settingsCouldNotBeSaved"),
            });
        } finally {
            setSaving(false);
        }
    };

    return <Card>
        <CardBody>
            <CardHeader title={t("tenants.tenantInvoiceSettings.title")} icon="mdi-tag-text-outline" />
            <div className="pt-3">
                {message && <Alert color={message.color} fade={false}>{message.text}</Alert>}
                <Label for="tenant-invoice-prefix">{t("tenants.tenantInvoiceSettings.invoicePrefix")}</Label>
                <Input
                    id="tenant-invoice-prefix"
                    value={prefix}
                    maxLength={20}
                    onChange={event => setPrefix(event.target.value)}
                />
                <small className="text-muted d-block mt-1 mb-3">
                    {t("tenants.tenantInvoiceSettings.invoicePrefixHelp")}
                </small>
                <Label for="tenant-invoice-name-template">{t("tenants.tenantInvoiceSettings.nameTemplate")}</Label>
                <Input
                    id="tenant-invoice-name-template"
                    value={template}
                    maxLength={120}
                    onChange={event => setTemplate(event.target.value)}
                />
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                    <small className="text-muted me-1">{t("tenants.tenantInvoiceSettings.presets")}</small>
                    <Button color="light" size="sm" type="button" onClick={() => setTemplate(defaultTemplate)}>
                        {t("tenants.tenantInvoiceSettings.yearMonth")}
                    </Button>
                    <Button
                        color="light"
                        size="sm"
                        type="button"
                        onClick={() => setTemplate("wk{weeknr} - {project.name}")}
                    >
                        {t("tenants.tenantInvoiceSettings.weekProject")}
                    </Button>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2">
                    <small className="text-muted align-self-center me-1">{t("tenants.tenantInvoiceSettings.placeholders")}</small>
                    {placeholders.map(placeholder =>
                        <Button
                            color="light"
                            size="sm"
                            type="button"
                            key={placeholder}
                            onClick={() => setTemplate(current => current + placeholder)}
                        >
                            {placeholder}
                        </Button>
                    )}
                </div>
                <div className="d-flex justify-content-between gap-3 border-bottom py-2 mt-2">
                    <span className="text-muted">{t("tenants.tenantInvoiceSettings.preview")}</span>
                    <span className="text-end text-break">{preview}</span>
                </div>
                <div className="d-flex justify-content-between gap-3 border-bottom py-2">
                    <span className="text-muted">{t("tenants.tenantInvoiceSettings.invoiceNumberPreview")}</span>
                    <span className="text-end text-break font-monospace">
                        {(prefix.trim() || defaultInvoicePrefix)}-{invoiceNumberYear}-0001<br />
                        {(prefix.trim() || defaultInvoicePrefix)}-12345678-{invoiceNumberYear}-0001
                    </span>
                </div>
                <small className="text-muted d-block mt-2">
                    {t("tenants.tenantInvoiceSettings.withoutAProjectNameAProjectTemplateAutomaticallyFallsBackToYearMonth")}
                </small>
                <FormGroup switch className="invoice-reminder-setting border rounded py-2 pe-3 mt-3 mb-0">
                    <Input
                        id="tenant-invoice-reminders-enabled"
                        type="switch"
                        checked={remindersEnabled}
                        onChange={event => setRemindersEnabled(event.target.checked)}
                    />
                    <Label check for="tenant-invoice-reminders-enabled" className="fw-semibold">
                        {t("tenants.tenantInvoiceSettings.invoiceReminders")}
                    </Label>
                    <small className="text-muted d-block mt-1">
                        {t("tenants.tenantInvoiceSettings.invoiceRemindersHelp")}
                    </small>
                </FormGroup>
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button color="primary" type="button" disabled={saving} onClick={save}>
                        {saving ? <Spinner size="sm" /> : t("common.save")}
                    </Button>
                </div>
            </div>
        </CardBody>
    </Card>;
};

export default TenantInvoiceSettings;
